/**
 * Layout Templates Panel
 *
 * Provides quick-select kitchen layout templates:
 * - L-Shape: Sink + fridge on one wall, cooktop on adjacent
 * - U-Shape: Work triangle across three walls
 * - Galley: Two parallel walls
 * - Island: Central island with perimeter appliances
 */

import { useCallback, useMemo } from "react";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { generateFallbackLayouts } from "@/services/layoutEngine";
import { cn } from "@/lib/utils";
import type { LayoutSuggestion } from "@byk/shared";

// Layout type icons (SVG paths)
const LAYOUT_ICONS: Record<string, React.ReactNode> = {
  "L-shape": (
    <svg className="h-12 w-12" viewBox="0 0 48 48" fill="none" stroke="currentColor">
      <path strokeWidth={3} d="M8 8v32h32" />
      <circle cx="12" cy="20" r="3" fill="currentColor" />
      <circle cx="12" cy="36" r="3" fill="currentColor" />
      <circle cx="28" cy="36" r="3" fill="currentColor" />
    </svg>
  ),
  "U-shape": (
    <svg className="h-12 w-12" viewBox="0 0 48 48" fill="none" stroke="currentColor">
      <path strokeWidth={3} d="M8 8v32h32V8" />
      <circle cx="12" cy="24" r="3" fill="currentColor" />
      <circle cx="24" cy="36" r="3" fill="currentColor" />
      <circle cx="36" cy="24" r="3" fill="currentColor" />
    </svg>
  ),
  Galley: (
    <svg className="h-12 w-12" viewBox="0 0 48 48" fill="none" stroke="currentColor">
      <path strokeWidth={3} d="M8 12h32M8 36h32" />
      <circle cx="16" cy="12" r="3" fill="currentColor" />
      <circle cx="32" cy="12" r="3" fill="currentColor" />
      <circle cx="24" cy="36" r="3" fill="currentColor" />
    </svg>
  ),
  Island: (
    <svg className="h-12 w-12" viewBox="0 0 48 48" fill="none" stroke="currentColor">
      <rect x="8" y="8" width="32" height="32" strokeWidth={2} />
      <rect x="18" y="18" width="12" height="12" strokeWidth={2} fill="rgba(59, 130, 246, 0.2)" />
      <circle cx="24" cy="24" r="3" fill="currentColor" />
      <circle cx="12" cy="24" r="3" fill="currentColor" />
      <circle cx="36" cy="24" r="3" fill="currentColor" />
    </svg>
  ),
};

interface LayoutTemplatesProps {
  className?: string;
}

export const LayoutTemplates: React.FC<LayoutTemplatesProps> = ({ className }) => {
  const {
    room,
    walls,
    elements,
    volumes,
    products,
    addVolume,
    clearAll,
    generateRectangularWalls,
  } = useFloorPlanStore();

  // Generate available layouts based on current room
  const layouts = useMemo(() => {
    if (!walls || walls.length === 0) {
      return [];
    }
    const response = generateFallbackLayouts(room, walls, elements, volumes, products);
    return response.layouts;
  }, [room, walls, elements, volumes, products]);

  // Apply a layout template
  const applyLayout = useCallback(
    (layout: LayoutSuggestion) => {
      // Clear existing volumes first
      clearAll();

      // Add volumes from layout placements
      for (const placement of layout.placements) {
        // Find the product for this placement
        const product = products.find((p) => p.id === placement.productId);
        if (!product) continue;

        // Add volume with position (rotation is set during placement if supported)
        addVolume(
          placement.productId,
          {
            x: placement.position.x,
            y: placement.position.y,
            z: placement.position.z,
          }
        );
      }
    },
    [addVolume, clearAll, products]
  );

  // Get score color
  const getScoreColor = (score: number): string => {
    if (score >= 0.8) return "text-green-600";
    if (score >= 0.5) return "text-amber-600";
    return "text-red-600";
  };

  // Get score label
  const getScoreLabel = (score: number): string => {
    if (score >= 0.8) return "Optimal";
    if (score >= 0.5) return "Good";
    return "Needs Work";
  };

  if (layouts.length === 0) {
    return (
      <div className={cn("rounded-lg bg-gray-50 p-4", className)}>
        <h3 className="mb-2 font-semibold text-gray-700">Layout Templates</h3>
        <p className="mb-3 text-sm text-gray-500">
          Draw room walls first, or generate walls from room dimensions.
        </p>
        <button
          onClick={generateRectangularWalls}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
          Generate Walls ({room.width}×{room.depth} cm)
        </button>
      </div>
    );
  }

  return (
    <div className={cn("rounded-lg bg-white p-4 shadow-sm", className)}>
      <h3 className="mb-3 font-semibold text-gray-700">Layout Templates</h3>
      <p className="mb-4 text-xs text-gray-500">
        Quick-start with professional kitchen layouts. Click to apply.
      </p>

      <div className="grid grid-cols-2 gap-3">
        {layouts.map((layout) => (
          <button
            key={layout.id}
            onClick={() => applyLayout(layout)}
            className="group flex flex-col items-center rounded-lg border border-gray-200 p-3 transition-all hover:border-blue-300 hover:bg-blue-50 hover:shadow-md"
            title={layout.reasoning}
          >
            {/* Layout Icon */}
            <div className="mb-2 text-gray-400 transition-colors group-hover:text-blue-500">
              {LAYOUT_ICONS[layout.type] || (
                <div className="flex h-12 w-12 items-center justify-center rounded bg-gray-100 text-xs">
                  {layout.type}
                </div>
              )}
            </div>

            {/* Layout Name */}
            <span className="text-sm font-medium text-gray-700">{layout.type}</span>

            {/* Score Badge */}
            <div className="mt-1 flex items-center gap-1">
              <div
                className={cn(
                  "h-2 w-2 rounded-full",
                  layout.score >= 0.8
                    ? "bg-green-500"
                    : layout.score >= 0.5
                    ? "bg-amber-500"
                    : "bg-red-500"
                )}
              />
              <span className={cn("text-xs", getScoreColor(layout.score))}>
                {getScoreLabel(layout.score)}
              </span>
            </div>

            {/* Work Triangle Info (on hover) */}
            {layout.triangle && (
              <div className="mt-2 hidden text-xs text-gray-400 group-hover:block">
                {Math.round(layout.triangle.perimeter / 100)}m perimeter
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-4 border-t border-gray-100 pt-3">
        <p className="text-xs text-gray-400">
          Layouts are scored by work triangle optimization (sink ↔ fridge ↔ cooktop).
        </p>
      </div>
    </div>
  );
};

export default LayoutTemplates;
